import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    Dialog,
    Tooltip,
    TextField,
    Autocomplete
} from '@mui/material';
import { Fullscreen, Close, FiberManualRecord, ExpandMore, ChevronRight, Download } from '@mui/icons-material';
import ServiciosCalificacion from './ServiciosCalificacion';

const ESTATUS_APROBADO = 1;
const ESTATUS_NO_APROBADO = 2;
const ESTATUS_NO_ENTREGADO = 3;

// Catalogo asignable por el administrador: solo estos tres
const CATALOGO_ESTATUS = [
    { value: ESTATUS_APROBADO, label: 'Aprobado', color: '#2e7d32' },
    { value: ESTATUS_NO_APROBADO, label: 'No aprobado', color: '#f9a825' },
    { value: ESTATUS_NO_ENTREGADO, label: 'No entregado', color: '#c62828' }
];

// No aprobado es el estatus por defecto hasta que el administrador revise
const ESTATUS_DEFAULT = CATALOGO_ESTATUS.find((item) => item.value === ESTATUS_NO_APROBADO);
const opcionesEstatusEvidencia = CATALOGO_ESTATUS.map(({ value, label }) => ({ value, label }));
const catalogoEstatusPorValor = CATALOGO_ESTATUS.reduce((acc, item) => ({ ...acc, [item.value]: item }), {});

const obtenerEstatus = (value) => catalogoEstatusPorValor[value] || ESTATUS_DEFAULT;
const colorEstatus = (value) => obtenerEstatus(value).color;

// Solo "No entregado" asigna 0% sin posibilidad de modificarlo
const permiteEditarCalificacion = (value) => Number(value) !== ESTATUS_NO_ENTREGADO;

// El backend ya resolvio el estatus (incluye el "No entregado" por plazo vencido)
const obtenerEstatusMaterial = (material) => obtenerEstatus(material.estatusCalificacion);

const detectarTipoArchivo = (url = '') => {
    if (/\.pdf($|\?)/i.test(url)) return 'pdf';
    if (/\.(mp4|webm|mov|avi|mkv|wmv|flv|m4v|3gp|mpeg|mpg|ogv)($|\?)/i.test(url)) return 'video';
    if (/\.(mp3|wav|ogg|m4a|aac|flac|opus|wma|aiff|amr)($|\?)/i.test(url)) return 'audio';
    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff|tif)($|\?)/i.test(url)) return 'imagen';
    return 'descargable';
};

const extraerNombreArchivo = (ruta = '') => {
    try {
        const sinQuery = ruta.split('?')[0];
        return decodeURIComponent(sinQuery.split('/').pop() || '');
    } catch (e) {
        return ruta;
    }
};

const extraerExtension = (ruta = '') => {
    const nombre = extraerNombreArchivo(ruta);
    const partes = nombre.split('.');
    return partes.length > 1 ? partes.pop().toLowerCase() : '';
};

const normalizarParaNombre = (texto = '') =>
    texto
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/[^a-zA-Z0-9\s_-]/g, '')
        .replace(/\s+/g, '_')
        .toLowerCase();

const formatearNombreEvidencia = (nombreColaborador, tituloMaterial, rutaArchivo) => {
    const extension = extraerExtension(rutaArchivo);
    const base = normalizarParaNombre(`${nombreColaborador}_${tituloMaterial}`);
    return extension ? `${base}.${extension}` : base;
};

const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    const partes = String(fecha).split(' ')[0].split('-');
    return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : fecha;
};

// La calificacion es un entero de 0 a 100
const normalizarCalificacionEntera = (valorCrudo) => {
    const soloDigitos = (valorCrudo || '').replace(/\D/g, '');

    if (soloDigitos === '') return '';

    const numero = parseInt(soloDigitos, 10);
    if (isNaN(numero)) return '';

    return String(Math.min(numero, 100));
};

const estiloBotonNotable = {
    backgroundColor: '#e0e0e0',
    color: '#000000',
    borderRadius: '999px',
    boxShadow: '0px 2px 6px rgba(0,0,0,0.25)',
    textTransform: 'none',
    px: 2,
    '&:hover': {
        backgroundColor: '#d5d5d5',
        boxShadow: '0px 3px 8px rgba(0,0,0,0.3)'
    }
};

const ModalEvidenciaColaborador = ({ colaborador, idCurso, nombreCurso, nombreGrupo }) => {
    const serviciosCalificacion = ServiciosCalificacion();

    const [sesiones, setSesiones] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [seleccion, setSeleccion] = useState(null);
    const [pantallaCompleta, setPantallaCompleta] = useState(false);
    const [descargando, setDescargando] = useState(null);
    const [estatusEvidencia, setEstatusEvidencia] = useState(ESTATUS_DEFAULT);
    const [calificacionPorcentaje, setCalificacionPorcentaje] = useState('0');
    const [comentarioEvidencia, setComentarioEvidencia] = useState('');
    const [enviandoCalificacion, setEnviandoCalificacion] = useState(false);
    const [mensajeEnvio, setMensajeEnvio] = useState(null);

    const [sesionesAbiertas, setSesionesAbiertas] = useState([]);
    const [actividadesAbiertas, setActividadesAbiertas] = useState([]);

    const cargarEvidencias = () => {
        serviciosCalificacion.getEvidenciaColaborador(
            (data) => {
                const listaSesiones = data.sesiones || [];
                setSesiones(listaSesiones);
                setCargando(false);

                setSesionesAbiertas(listaSesiones.map((s) => s.idSesion));
                const todasLasActividades = listaSesiones.flatMap((s) => s.actividades.map((a) => a.idActividad));
                setActividadesAbiertas(todasLasActividades);
            },
            { idCurso, idColaborador: colaborador.idColaborador }
        );
    };

    useEffect(() => {
        cargarEvidencias();
    }, [idCurso]);

    const toggleSesion = (idSesion) => {
        setSesionesAbiertas((prev) => (prev.includes(idSesion) ? prev.filter((id) => id !== idSesion) : [...prev, idSesion]));
    };

    const toggleActividad = (idActividad) => {
        setActividadesAbiertas((prev) => (prev.includes(idActividad) ? prev.filter((id) => id !== idActividad) : [...prev, idActividad]));
    };

    const aplicarEstadoPrevio = (estado) => {
        const estatusPrevio = estado ? obtenerEstatus(estado.estatusCalificacion) : ESTATUS_DEFAULT;
        setEstatusEvidencia(estatusPrevio);

        // La calificacion llega como decimal desde la BD en pantalla se muestra entera
        const califPrevia = estado?.calificacion != null ? Math.round(Number(estado.calificacion)) : 0;
        setCalificacionPorcentaje(permiteEditarCalificacion(estatusPrevio.value) ? String(califPrevia) : '0');
        setComentarioEvidencia(estado?.retroalimentacion || '');
    };

    const verMaterial = (material, tituloActividad) => {
        setMensajeEnvio(null);
        setSeleccion({
            idDetalleActividad: material.idDetalleActividad,
            titulo: material.titulo,
            tituloActividad,
            archivoEvidencia: material.archivoEvidencia,
            fechaLimite: material.fechaLimite,
            plazoVencido: Number(material.plazoVencido) === 1,
            cargando: true
        });

        serviciosCalificacion.getMaterialEvidencia(
            (res) => {
                if (res.status !== 1) {
                    const estado = res.estadoEntrega;
                    aplicarEstadoPrevio(estado);
                    setSeleccion((prev) => ({
                        ...prev,
                        sinEvidencia: true,
                        mensaje: res.mensaje,
                        fechaLimite: estado?.fechaLimite || prev.fechaLimite,
                        plazoVencido: Number(estado?.plazoVencido) === 1,
                        cargando: false
                    }));
                    return;
                }

                aplicarEstadoPrevio(res.calificacionEvidencia);
                setSeleccion((prev) => ({
                    ...prev,
                    material: res.material,
                    fechaLimite: res.estadoEntrega?.fechaLimite || prev.fechaLimite,
                    cargando: false
                }));
            },
            { idDetalleActividad: material.idDetalleActividad, idColaborador: colaborador.idColaborador }
        );
    };

    const abrirPantallaCompleta = () => setPantallaCompleta(true);
    const cerrarPantallaCompleta = () => setPantallaCompleta(false);

    const handleCambioEstatus = (item) => {
        const nuevoEstatus = item || ESTATUS_DEFAULT;
        setEstatusEvidencia(nuevoEstatus);

        // No entregado fuerza 0% sin posibilidad de modificarlo
        if (!permiteEditarCalificacion(nuevoEstatus.value)) {
            setCalificacionPorcentaje('0');
        }
    };

    const handleCambioCalificacion = (event) => {
        setCalificacionPorcentaje(normalizarCalificacionEntera(event.target.value));
    };

    const handleBlurCalificacion = () => {
        if (calificacionPorcentaje === '') {
            setCalificacionPorcentaje('0');
        }
    };

    const handleCambioComentario = (event) => {
        const textoLimpio = (event.target.value || '').replace(/[^a-zA-Z0-9\sÁÉÍÓÚáéíóúÑñ]/g, '').slice(0, 450);
        setComentarioEvidencia(textoLimpio);
    };

    const descargarEvidencia = (material) => {
        if (descargando) return;
        setDescargando(material.idDetalleActividad);

        const nombreDescarga = formatearNombreEvidencia(colaborador.nombre_persona, material.titulo, material.archivoEvidencia || '');

        serviciosCalificacion.getUrlDescargaEvidencia(
            (res) => {
                setDescargando(null);
                if (res.status === 1 && res.url) {
                    window.location.href = res.url;
                }
            },
            {
                idDetalleActividad: material.idDetalleActividad,
                idColaborador: colaborador.idColaborador,
                nombreDescarga
            }
        );
    };

    const enviarCalificacionEvidencia = () => {
        if (!seleccion?.idDetalleActividad) return;

        setEnviandoCalificacion(true);
        setMensajeEnvio(null);

        const calificacionEnviar = permiteEditarCalificacion(estatusEvidencia.value) ? parseInt(calificacionPorcentaje || '0', 10) : 0;

        serviciosCalificacion.guardarCalificacionEvidencia(
            (res) => {
                setEnviandoCalificacion(false);

                const status = res?.status !== undefined ? Number(res.status) : 1;
                const guardado = status === 1;

                setMensajeEnvio({
                    status: guardado ? 1 : 0,
                    texto: res?.mensaje || (guardado ? 'Calificación guardada correctamente.' : 'No se pudo guardar la calificación.')
                });

                if (guardado) {
                    cargarEvidencias();
                }
            },
            {
                idDetalleActividad: seleccion.idDetalleActividad,
                idColaborador: colaborador.idColaborador,
                estatusCalificacion: estatusEvidencia.value,
                calificacion: calificacionEnviar,
                retroalimentacion: comentarioEvidencia || null
            }
        );
    };

    const renderMaterial = (material, alturaMax = 360) => {
        const url = material.linkVideo;
        if (!url) {
            return (
                <Typography variant="body2" color="textSecondary">
                    No fue posible obtener el archivo de evidencia.
                </Typography>
            );
        }
        const tipo = detectarTipoArchivo(url);

        if (tipo === 'pdf') {
            const srcPdf = material.pdf ? `data:application/pdf;base64,${material.pdf}` : url;
            return <iframe src={srcPdf} title="Evidencia PDF" width="100%" height={alturaMax} style={{ border: 'none' }} />;
        }
        if (tipo === 'video') {
            return (
                <video controls width="100%" style={{ maxHeight: alturaMax }}>
                    <source src={url} />
                </video>
            );
        }
        if (tipo === 'audio') {
            return (
                <audio controls style={{ width: '100%' }}>
                    <source src={url} />
                </audio>
            );
        }
        if (tipo === 'imagen') {
            return <Box component="img" src={url} alt="Evidencia" sx={{ width: '100%', maxHeight: alturaMax, objectFit: 'contain' }} />;
        }
        return (
            <Button variant="text" href={url} target="_blank" rel="noreferrer" startIcon={<Download />} sx={estiloBotonNotable}>
                Descargar evidencia
            </Button>
        );
    };

    const renderPanelCalificacion = () => (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Estatus de evidencia
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Autocomplete
                    id="estatus-evidencia"
                    size="small"
                    fullWidth
                    disableClearable
                    options={opcionesEstatusEvidencia}
                    value={estatusEvidencia}
                    onChange={(_, item) => handleCambioEstatus(item)}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={(option) => option?.label || ''}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <Box
                                component="span"
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: colorEstatus(option.value),
                                    display: 'inline-block',
                                    mr: 1
                                }}
                            />
                            {option.label}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Selecciona un estatus" />}
                />
                <TextField
                    id="calificacion-porcentaje"
                    label="Calificación (%)"
                    size="small"
                    fullWidth
                    value={calificacionPorcentaje}
                    onChange={handleCambioCalificacion}
                    onBlur={handleBlurCalificacion}
                    disabled={!permiteEditarCalificacion(estatusEvidencia.value)}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 3 }}
                    helperText={
                        permiteEditarCalificacion(estatusEvidencia.value)
                            ? 'Número entero de 0 a 100'
                            : 'No entregado asigna 0% automáticamente'
                    }
                />
            </Box>

            <Box sx={{ mt: 1.5 }}>
                <TextField
                    id="comentario-evidencia"
                    label="Retroalimentación"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    value={comentarioEvidencia}
                    onChange={handleCambioComentario}
                    sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '12px !important' },
                        '& .MuiOutlinedInput-notchedOutline': { borderRadius: '12px !important' },
                        '& fieldset': { borderRadius: '12px !important' },
                        '& textarea': { borderRadius: '12px !important' }
                    }}
                    helperText={`${comentarioEvidencia.length}/450. Solo letras, numeros y espacios.`}
                />
            </Box>

            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
                {mensajeEnvio && (
                    <Typography variant="caption" sx={{ color: mensajeEnvio.status === 1 ? '#2e7d32' : '#c62828' }}>
                        {mensajeEnvio.texto}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    disabled={enviandoCalificacion}
                    onClick={enviarCalificacionEvidencia}
                    startIcon={enviandoCalificacion ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {enviandoCalificacion ? 'Enviando...' : 'Enviar calificación'}
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Evidencia del curso y grupo seleccionado:
                </Typography>
                <List dense disablePadding>
                    <ListItem disableGutters sx={{ py: 0.25 }}>
                        <FiberManualRecord sx={{ fontSize: 6, mr: 1 }} />
                        <ListItemText
                            primary={
                                <Typography variant="body2">
                                    Grupo: <strong>{nombreGrupo || '-'}</strong>
                                </Typography>
                            }
                        />
                    </ListItem>
                    <ListItem disableGutters sx={{ py: 0.25 }}>
                        <FiberManualRecord sx={{ fontSize: 6, mr: 1 }} />
                        <ListItemText
                            primary={
                                <Typography variant="body2">
                                    Curso: <strong>{nombreCurso || '-'}</strong>
                                </Typography>
                            }
                        />
                    </ListItem>
                </List>
            </Box>

            <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}>
                <Paper variant="outlined" sx={{ flex: '0 0 40%', p: 1.5, borderRadius: 2, maxHeight: 460, overflowY: 'auto' }}>
                    {cargando ? (
                        <CircularProgress size={24} />
                    ) : sesiones.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            No hay materiales que soliciten evidencia registrados para este curso.
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {sesiones.map((sesion) => (
                                <div key={sesion.idSesion} style={{ marginBottom: 4 }}>
                                    <ListItemButton onClick={() => toggleSesion(sesion.idSesion)} sx={{ borderRadius: 1 }}>
                                        <IconButton size="small" edge="start" sx={{ mr: 0.5 }}>
                                            {sesionesAbiertas.includes(sesion.idSesion) ? <ExpandMore /> : <ChevronRight />}
                                        </IconButton>
                                        <ListItemText primary={sesion.titulo} primaryTypographyProps={{ fontWeight: 500 }} />
                                    </ListItemButton>

                                    <Collapse in={sesionesAbiertas.includes(sesion.idSesion)} timeout="auto" unmountOnExit>
                                        <List dense disablePadding sx={{ pl: 2 }}>
                                            {sesion.actividades.map((actividad) => (
                                                <div key={actividad.idActividad}>
                                                    <ListItemButton
                                                        onClick={() => toggleActividad(actividad.idActividad)}
                                                        sx={{ borderRadius: 1 }}
                                                    >
                                                        <IconButton size="small" edge="start" sx={{ mr: 0.5 }}>
                                                            {actividadesAbiertas.includes(actividad.idActividad) ? (
                                                                <ExpandMore />
                                                            ) : (
                                                                <ChevronRight />
                                                            )}
                                                        </IconButton>
                                                        <ListItemText primary={actividad.titulo} />
                                                    </ListItemButton>

                                                    <Collapse
                                                        in={actividadesAbiertas.includes(actividad.idActividad)}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <List dense disablePadding sx={{ pl: 3 }}>
                                                            {actividad.materiales.map((material) => {
                                                                const estatusMaterial = obtenerEstatusMaterial(material);
                                                                return (
                                                                    <ListItemButton
                                                                        key={material.idDetalleActividad}
                                                                        selected={
                                                                            seleccion?.idDetalleActividad === material.idDetalleActividad
                                                                        }
                                                                        onClick={() => verMaterial(material, actividad.titulo)}
                                                                        sx={{ borderRadius: 1 }}
                                                                    >
                                                                        <ListItemText
                                                                            primary={
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    <Tooltip
                                                                                        arrow
                                                                                        placement="right"
                                                                                        slotProps={{
                                                                                            tooltip: {
                                                                                                sx: {
                                                                                                    backgroundColor: 'transparent',
                                                                                                    p: 0,
                                                                                                    m: 0
                                                                                                }
                                                                                            },
                                                                                            arrow: { sx: { color: '#ffffff' } }
                                                                                        }}
                                                                                        title={
                                                                                            <Box
                                                                                                sx={{
                                                                                                    backgroundColor: '#ffffff',
                                                                                                    color: '#1f2937',
                                                                                                    border: '1px solid #e5e7eb',
                                                                                                    borderRadius: 1,
                                                                                                    px: 1.25,
                                                                                                    py: 1,
                                                                                                    boxShadow: '0 6px 18px rgba(0,0,0,0.12)'
                                                                                                }}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    component="div"
                                                                                                >
                                                                                                    {`Estatus: "${estatusMaterial.label}"`}
                                                                                                </Typography>
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    component="div"
                                                                                                >
                                                                                                    {`Calificación: "${
                                                                                                        material.calificacion != null
                                                                                                            ? Math.round(
                                                                                                                  Number(
                                                                                                                      material.calificacion
                                                                                                                  )
                                                                                                              ) + '%'
                                                                                                            : '-'
                                                                                                    }"`}
                                                                                                </Typography>
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    component="div"
                                                                                                >
                                                                                                    {`Fecha límite: "${formatearFecha(
                                                                                                        material.fechaLimite
                                                                                                    )}"`}
                                                                                                </Typography>
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    component="div"
                                                                                                >
                                                                                                    {`Comentarios: "${
                                                                                                        material.retroalimentacion || '-'
                                                                                                    }"`}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        }
                                                                                    >
                                                                                        <Box
                                                                                            component="span"
                                                                                            sx={{
                                                                                                width: 10,
                                                                                                height: 10,
                                                                                                borderRadius: '50%',
                                                                                                backgroundColor: estatusMaterial.color,
                                                                                                flexShrink: 0
                                                                                            }}
                                                                                        />
                                                                                    </Tooltip>
                                                                                    <Typography variant="body2">
                                                                                        {material.titulo}
                                                                                    </Typography>
                                                                                </Box>
                                                                            }
                                                                            secondary={
                                                                                material.archivoEvidencia
                                                                                    ? formatearNombreEvidencia(
                                                                                          colaborador.nombre_persona,
                                                                                          material.titulo,
                                                                                          material.archivoEvidencia
                                                                                      )
                                                                                    : Number(material.plazoVencido) === 1
                                                                                    ? `No entregado. Venció el ${formatearFecha(
                                                                                          material.fechaLimite
                                                                                      )}`
                                                                                    : `Sin evidencia. Plazo hasta el ${formatearFecha(
                                                                                          material.fechaLimite
                                                                                      )}`
                                                                            }
                                                                        />
                                                                        {material.archivoEvidencia && (
                                                                            <Tooltip title="Descargar evidencia">
                                                                                <span>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        disabled={
                                                                                            descargando === material.idDetalleActividad
                                                                                        }
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            descargarEvidencia(material);
                                                                                        }}
                                                                                    >
                                                                                        {descargando === material.idDetalleActividad ? (
                                                                                            <CircularProgress size={18} />
                                                                                        ) : (
                                                                                            <Download fontSize="small" />
                                                                                        )}
                                                                                    </IconButton>
                                                                                </span>
                                                                            </Tooltip>
                                                                        )}
                                                                    </ListItemButton>
                                                                );
                                                            })}
                                                        </List>
                                                    </Collapse>
                                                </div>
                                            ))}
                                        </List>
                                    </Collapse>
                                </div>
                            ))}
                        </List>
                    )}
                </Paper>

                <Paper
                    variant="outlined"
                    sx={{ flex: '1 1 60%', p: 1.5, borderRadius: 2, minHeight: 460, display: 'flex', flexDirection: 'column' }}
                >
                    {!seleccion ? (
                        <Box sx={{ m: 'auto', textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                Selecciona un material para ver la evidencia cargada por el colaborador.
                            </Typography>
                        </Box>
                    ) : seleccion.cargando ? (
                        <Box sx={{ m: 'auto' }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : seleccion.sinEvidencia ? (
                        <>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                <Box>
                                    <Typography variant="body2" sx={{ color: seleccion.plazoVencido ? '#c62828' : 'text.secondary' }}>
                                        {seleccion.plazoVencido
                                            ? `No entregado. El plazo venció el ${formatearFecha(seleccion.fechaLimite)}.`
                                            : 'El colaborador aún no ha cargado evidencia para este material.'}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                        Fecha límite: {formatearFecha(seleccion.fechaLimite)}
                                    </Typography>
                                </Box>
                            </Box>
                            {renderPanelCalificacion()}
                        </>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    mb: 1.5,
                                    pb: 1.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <List dense disablePadding>
                                    <ListItem disableGutters sx={{ py: 0.25 }}>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2">
                                                    Actividad: <strong>{seleccion.tituloActividad || '-'}</strong>
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters sx={{ py: 0.25 }}>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2">
                                                    Material:{' '}
                                                    <strong>
                                                        {seleccion.archivoEvidencia
                                                            ? formatearNombreEvidencia(
                                                                  colaborador.nombre_persona,
                                                                  seleccion.titulo,
                                                                  seleccion.archivoEvidencia
                                                              )
                                                            : seleccion.titulo}
                                                    </strong>
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    {seleccion.material?.fechaCreacion && (
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="caption" color="textSecondary">
                                                        Subido el: {seleccion.material.fechaCreacion}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    )}
                                </List>

                                {seleccion.material?.linkVideo && (
                                    <Button
                                        size="small"
                                        startIcon={<Fullscreen />}
                                        onClick={abrirPantallaCompleta}
                                        sx={{ flexShrink: 0, ...estiloBotonNotable }}
                                    >
                                        Ver en pantalla completa
                                    </Button>
                                )}
                            </Box>
                            <Box sx={{ flex: 1 }}>{renderMaterial(seleccion.material)}</Box>
                            {renderPanelCalificacion()}
                        </>
                    )}
                </Paper>
            </Box>

            <Dialog fullScreen open={pantallaCompleta} onClose={cerrarPantallaCompleta} disableEscapeKeyDown={false}>
                <Box onClick={cerrarPantallaCompleta} sx={{ position: 'fixed', inset: 0, backgroundColor: '#000', zIndex: 1 }} />

                <IconButton
                    onClick={cerrarPantallaCompleta}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        right: 16,
                        zIndex: 3,
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        color: '#fff',
                        width: 48,
                        height: 48,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                    }}
                >
                    <Close />
                </IconButton>

                <Box
                    sx={{
                        position: 'fixed',
                        top: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 3,
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        color: '#fff',
                        px: 2,
                        py: 0.75,
                        borderRadius: '999px',
                        pointerEvents: 'none'
                    }}
                >
                    <Typography variant="caption">Presiona ESC o toca la X para salir</Typography>
                </Box>

                <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4
                    }}
                >
                    {seleccion?.material && renderMaterial(seleccion.material, window.innerHeight - 140)}
                </Box>
            </Dialog>
        </Box>
    );
};

export default ModalEvidenciaColaborador;
